// Simple car setup (DESIGN.md §5 v2): a couple of intuitive trade-offs rather
// than a full garage. Saved on the device and used in every mode.

import type { CarMods } from '../sim/car';

export interface CarSetup {
  /** −2 (low downforce, fast on straights) .. +2 (high downforce, more grip). */
  wing: number;
  /** Front brake bias, 0.50–0.62. */
  bias: number;
}

const KEY = 'fone.setup';
export const DEFAULT_SETUP: CarSetup = { wing: 0, bias: 0.56 };

export function loadSetup(): CarSetup {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) {
      const v = JSON.parse(raw) as Partial<CarSetup>;
      return {
        wing: Math.max(-2, Math.min(2, Math.round(v.wing ?? 0))),
        bias: Math.max(0.5, Math.min(0.62, v.bias ?? DEFAULT_SETUP.bias)),
      };
    }
  } catch {
    // Defaults.
  }
  return { ...DEFAULT_SETUP };
}

export function saveSetup(s: CarSetup): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(s));
  } catch {
    // Not persisted.
  }
}

/**
 * Downforce and drag multipliers for a wing level: each step trades ~4 % downforce
 * for ~12 % drag (both aero modes), so High is ~20 km/h slower at the end of the
 * straights but quicker through the long corners.
 */
export function wingFactors(wing: number): { downforce: number; drag: number } {
  return { downforce: 1 + 0.04 * wing, drag: 1 + 0.12 * wing };
}

/** Apply setup on top of whatever else is in the mods (damage multiplies in). */
export function applySetup(mods: CarMods, s: CarSetup): void {
  const f = wingFactors(s.wing);
  mods.downforce *= f.downforce;
  mods.drag *= f.drag;
  mods.brakeBias = s.bias;
}

export function wingLabel(w: number): string {
  return ['Low', 'Low-mid', 'Balanced', 'Mid-high', 'High'][w + 2] ?? 'Balanced';
}
